import { API_BASE, getCsrfToken } from '../config';
import React, { useState, useEffect, useCallback } from 'react';
import { Home, Receipt, FileText, CheckCircle, BarChart3, Plus } from 'lucide-react';
import { CURRENCIES, getCurrencyInfo, convertToEUR } from '../utils/currency';
import { useTranslation } from '../i18n/LanguageContext';

/** Justificatif : URL Supabase absolue ou chemin API relatif (ancien stockage local) */
function expenseReceiptHref(file) {
  const p = file?.path || '';
  if (/^https?:\/\//i.test(String(p))) return p;
  const rel = String(p).startsWith('/') ? p : `/${p}`;
  return `${API_BASE}${rel}`;
}

// Catégories par défaut (traductions via t() dans le composant)
const DEFAULT_EXPENSE_CATEGORY_KEYS = [
  { key: 'category_travel', daily_limit: 200 },
  { key: 'category_meals', daily_limit: 50 },
  { key: 'category_accommodation', daily_limit: 200 },
  { key: 'category_materials', daily_limit: 100 },
  { key: 'category_training', daily_limit: 500 },
  { key: 'category_representation', daily_limit: 150 },
  { key: 'category_missions', daily_limit: 300 }
];

const NotesFraisManagement = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('mes-notes');
  const [notes, setNotes] = useState([]);
  const [pendingNotes, setPendingNotes] = useState([]);

  // Map API category name (FR or EN) to translation key so dropdown shows correct language
  const categoryNameToKey = {
    'Déplacement': 'category_travel', 'Travel': 'category_travel',
    'Repas': 'category_meals', 'Meals': 'category_meals',
    'Hébergement': 'category_accommodation', 'Accommodation': 'category_accommodation',
    'Matériel / activité': 'category_materials', 'Materials / Activity': 'category_materials',
    'Formation': 'category_training', 'Training': 'category_training',
    'Représentation': 'category_representation', 'Representation': 'category_representation',
    'Missions / séminaires': 'category_missions', 'Missions / Seminars': 'category_missions'
  };
  
  // Créer les catégories avec les noms traduits (name = value sent to API, key = for t())
  const getDefaultCategoriesList = useCallback(() => {
    return DEFAULT_EXPENSE_CATEGORY_KEYS.map(cat => ({
      name: t(`expenses.${cat.key}`),
      key: cat.key,
      description: t(`expenses.${cat.key}`),
      daily_limit: cat.daily_limit
    }));
  }, [t]);
  
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedReceipts, setSelectedReceipts] = useState([]);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);
  const [sendingId, setSendingId] = useState(null);
  const [modifyNote, setModifyNote] = useState(null);
  const [modifyAmount, setModifyAmount] = useState('');
  const [modifyComment, setModifyComment] = useState('');

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const check = () => setIsMobile(mql.matches);
    check();
    mql.addEventListener('change', check);
    return () => mql.removeEventListener('change', check);
  }, []);

  // Formulaire création note
  const [newNote, setNewNote] = useState({
    title: '',
    description: '',
    expense_date: '',
    purpose: '',
    project_code: '',
    items: [{ category: '', description: '', amount: '', currency: 'EUR', merchant_name: '', receipt_reference: '' }]
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => setUser(data.user))
      .catch(console.error);
    }
    // Initialiser les catégories avec les traductions
    setCategories(getDefaultCategoriesList());
  }, [t, getDefaultCategoriesList]);

  // Charger le nombre de notes en attente pour le badge Validation (admin)
  useEffect(() => {
    if (user?.role === 'admin') {
      loadPendingNotes(true);
    }
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCategories();
    if (activeTab === 'mes-notes') {
      loadMesNotes();
    } else if (activeTab === 'validation' && user?.role === 'admin') {
      loadPendingNotes();
    } else if (activeTab === 'stats' && user?.role === 'admin') {
      loadStats();
    }
  }, [activeTab, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMesNotes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/notes-frais/mes-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setNotes(data.notes || []);
    } catch (error) {
      console.error('Erreur chargement notes:', error);
    }
    setLoading(false);
  };

  const loadPendingNotes = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/notes-frais/pending-validation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setPendingNotes(data.notes || []);
    } catch (error) {
      console.error('Erreur chargement notes en attente:', error);
    }
    if (!silent) setLoading(false);
  };

  const handleSendReport = async (note) => {
    if (note.status !== 'draft') return;
    if (!window.confirm(t('expenses.submit_confirm'))) return;
    setSendingId(note.id);
    try {
      const token = localStorage.getItem('token');
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('expenses.error_submission') || 'Please refresh the page and try again.'));
        setSendingId(null);
        return;
      }
      const response = await fetch(`${API_BASE}/api/notes-frais/${note.id}/submit`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf }
      });
      const data = await response.json();
      if (response.ok && data.note) {
        loadMesNotes();
        alert(t('expenses.send_success'));
      } else {
        alert(data.error || t('expenses.error_submission'));
      }
    } catch (error) {
      console.error('Erreur envoi note:', error);
      alert(t('expenses.error_submission'));
    }
    setSendingId(null);
  };

  const loadCategories = async () => {
    // Catégories par défaut en cas d'échec
    const defaultCategories = getDefaultCategoriesList();
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('⚠️ Token manquant - Utilisation catégories par défaut');
        setCategories(defaultCategories);
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/notes-frais/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.warn('⚠️ Erreur API catégories - Utilisation catégories par défaut');
        setCategories(defaultCategories);
        return;
      }
      
      const data = await response.json();
      const raw = data.categories && Array.isArray(data.categories) && data.categories.length > 0
        ? data.categories
        : defaultCategories;
      const categories = raw.map(c => ({
        ...c,
        key: c.key || categoryNameToKey[c.name] || null
      }));
      setCategories(categories);
      console.log(`✅ ${categories.length} ${t('expenses.category').toLowerCase()}s chargées`);
    } catch (error) {
      console.error('❌ Erreur chargement catégories:', error);
      // Toujours définir les catégories par défaut en cas d'erreur
      setCategories(defaultCategories);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/notes-frais/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
    setLoading(false);
  };

  const handleCreateNote = async () => {
  console.log('📝 Données à envoyer:', newNote);
  
  // Validation côté frontend
  if (!newNote.title || !newNote.title.trim()) {
    alert('❌ ' + t('expenses.title_required'));
    return;
  }

  if (!newNote.expense_date) {
    alert('❌ ' + t('expenses.expense_date_required'));
    return;
  }

  if (!newNote.items || newNote.items.length === 0) {
    alert('❌ ' + t('expenses.at_least_one_expense_required'));
    return;
  }

  // Validation des items
  for (let i = 0; i < newNote.items.length; i++) {
    const item = newNote.items[i];
    if (!item.category || !item.description || !item.amount) {
      alert(`❌ ${t('expenses.line_required', { index: i + 1 })}`);
      return;
    }
    if (isNaN(parseFloat(item.amount)) || parseFloat(item.amount) <= 0) {
      alert(`❌ ${t('expenses.amount_invalid', { index: i + 1 })}`);
      return;
    }
  }

  setLoading(true);
  try {
    const token = localStorage.getItem('token');
    console.log('🔑 Token:', token ? 'Présent' : 'Absent');
    
    // Vérifier que les items sont valides avant l'envoi
    const validItems = newNote.items
      .filter(item => 
        item.category && item.category.trim() && 
        item.description && item.description.trim() && 
        item.amount && !isNaN(parseFloat(item.amount)) && parseFloat(item.amount) > 0
      )
      .map(item => ({ ...item, currency: item.currency || 'EUR' }));
    
    if (validItems.length === 0) {
      alert('❌ ' + t('expenses.at_least_one_complete_line_required'));
      setLoading(false);
      return;
    }
    
    console.log('📝 Items à envoyer (avec currency):', validItems);

    // Toujours utiliser le backend pour créer la note (currency + total sauvegardés en DB)
    const formData = new FormData();
    formData.append('title', newNote.title);
    formData.append('description', newNote.description || '');
    formData.append('expense_date', newNote.expense_date);
    formData.append('purpose', newNote.purpose || '');
    formData.append('project_code', newNote.project_code || '');
    formData.append('items', JSON.stringify(validItems));

    selectedReceipts.forEach((file) => {
      formData.append('receipts', file);
    });

    const csrf = await getCsrfToken();
    if (!csrf) {
      alert('❌ ' + (t('expenses.error_connection') || 'Please refresh the page and try again.'));
      setLoading(false);
      return;
    }
    const response = await fetch(`${API_BASE}/api/notes-frais/create`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf },
      body: formData
    });

    const data = await response.json();

    if (response.ok && data.success) {
      alert('✅ ' + data.message);
      setShowCreateModal(false);
      setNewNote({
        title: '',
        description: '',
        expense_date: '',
        purpose: '',
        project_code: '',
        items: [{ category: '', description: '', amount: '', currency: 'EUR', merchant_name: '', receipt_reference: '' }]
      });
      setSelectedReceipts([]);
      loadMesNotes();
    } else {
      alert('❌ ' + (data.error || t('expenses.unknown_error')));
    }
  } catch (error) {
    console.error('❌ Erreur réseau:', error);
    alert('❌ ' + t('expenses.error_connection'));
  } finally {
    setLoading(false);
  }
};

  const handleValidation = async (noteId, action, approvedAmount = null, validationComment = null) => {
    try {
      const token = localStorage.getItem('token');
      const csrf = await getCsrfToken();
      if (!csrf) {
        alert('❌ ' + (t('expenses.error_connection') || 'Please refresh the page and try again.'));
        return;
      }
      const body = { action };
      
      if (action === 'reject') {
        const reason = prompt(t('expenses.rejection_reason_prompt') || 'Reason for refusal (optional):');
        if (reason === null) return; // user cancelled
        body.rejection_reason = reason || '';
      } else {
        if (approvedAmount !== null && approvedAmount !== '') body.approved_amount = parseFloat(approvedAmount);
        if (validationComment != null && String(validationComment).trim() !== '') body.validation_comment = String(validationComment).trim();
      }

      const response = await fetch(`${API_BASE}/api/notes-frais/${noteId}/validate`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-CSRF-Token': csrf
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(t('formations.non_json_response'));
      }

      const data = await response.json();
      
      alert('✅ ' + data.message);
      loadPendingNotes();
    } catch (error) {
      console.error('Erreur validation note de frais:', error);
      alert('❌ ' + (error.message || t('expenses.error_validation')));
    }
  };

  const addItem = () => {
    setNewNote(prev => ({
      ...prev,
      items: [...prev.items, { category: '', description: '', amount: '', merchant_name: '', receipt_reference: '' }]
    }));
  };

  const removeItem = (index) => {
    setNewNote(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setNewNote(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const getStatusColor = (status) => {
    const norm = normalizeStatus(status);
    switch (norm) {
      case 'draft': return '#6b7280';
      case 'pending': return '#f59e0b';
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'paid': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  // Normalize status: backend sends 'draft' but sometimes key like 'expenses.status_draft' may appear
  const normalizeStatus = (s) => {
    if (!s || typeof s !== 'string') return 'draft';
    const lower = s.toLowerCase();
    if (lower === 'draft' || lower.endsWith('.status_draft') || lower === 'status_draft') return 'draft';
    if (lower === 'pending' || lower.endsWith('.status_pending') || lower === 'status_pending') return 'pending';
    if (lower === 'approved' || lower.endsWith('.status_approved') || lower === 'status_approved') return 'approved';
    if (lower === 'rejected' || lower.endsWith('.status_rejected') || lower === 'status_rejected') return 'rejected';
    if (lower === 'paid' || lower.endsWith('.status_paid') || lower === 'status_paid') return 'paid';
    return lower;
  };

  // Never show raw key: if t() returns key (e.g. "expenses.status_draft"), use fallback label
  const getStatusText = (status) => {
    const norm = normalizeStatus(status);
    const labels = {
      draft: t('expenses.status_draft'),
      pending: t('expenses.status_pending'),
      approved: t('expenses.status_approved'),
      rejected: t('expenses.status_rejected'),
      paid: t('expenses.status_paid')
    };
    const fallbacks = { draft: 'Draft', pending: 'Pending', approved: 'Approved', rejected: 'Rejected', paid: 'Paid' };
    if (norm in labels) {
      const v = labels[norm];
      if (v && !v.includes('expenses.') && !v.includes('status_')) return v;
      return fallbacks[norm];
    }
    return norm.charAt(0).toUpperCase() + norm.slice(1);
  };

  const formatNoteAmount = (amount, currencyCode) => {
    const info = getCurrencyInfo(currencyCode || 'EUR');
    const num = parseFloat(amount);
    if (amount == null || isNaN(num)) return '—';
    const code = currencyCode || 'EUR';
    const decimals = (code === 'FCFA' || code === 'XOF' || code === 'XAF') ? 0 : 2;
    const str = num.toFixed(decimals);
    return (code === 'EUR' || code === 'USD' || code === 'GBP') ? `${info.symbol}${str}` : `${str} ${info.symbol}`;
  };

  const itemsWithAmount = newNote.items.filter((item) => item.amount != null && item.amount !== '' && !isNaN(parseFloat(item.amount)));
  const currenciesUsed = [...new Set(itemsWithAmount.map((item) => item.currency || 'EUR'))];
  const singleCurrency = currenciesUsed.length === 1 ? currenciesUsed[0] : null;
  const totalAmount = singleCurrency
    ? itemsWithAmount.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    : itemsWithAmount.reduce((sum, item) => sum + convertToEUR(parseFloat(item.amount || 0), item.currency || 'EUR'), 0);
  const totalCurrency = singleCurrency || 'EUR';
  const totalSymbol = getCurrencyInfo(totalCurrency).symbol;
  const totalFormatted = singleCurrency
    ? totalAmount.toFixed(totalCurrency === 'FCFA' || totalCurrency === 'XOF' || totalCurrency === 'XAF' ? 0 : 2)
    : totalAmount.toFixed(2);

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header: sticky only on large screen; on mobile it scrolls with content */}
      <div className="notes-frais-header" style={{
        position: isMobile ? 'relative' : 'fixed',
        top: isMobile ? undefined : 0,
        left: isMobile ? undefined : 0,
        right: isMobile ? undefined : 0,
        padding: isMobile ? '16px 16px 12px' : '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: isMobile ? 0 : 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('common.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Receipt size={32} />
            {t('expenses.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('expenses.subtitle')}
        </p>
        </div>

      {/* Contenu principal avec padding pour le header (large screen only) */}
      <div className="notes-frais-content" style={{
        paddingTop: isMobile ? '8px' : '140px',
        paddingBottom: '40px',
        paddingLeft: isMobile ? '16px' : '20px',
        paddingRight: isMobile ? '16px' : '20px',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        {/* Navigation Tabs modernes */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          overflowX: 'auto',
          paddingBottom: '8px'
        }}>
            <button
              onClick={() => setActiveTab('mes-notes')}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              background: activeTab === 'mes-notes'
                ? 'linear-gradient(135deg, #ffc107, #ffd54f)'
                : 'rgba(255, 255, 255, 0.1)',
              color: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: activeTab === 'mes-notes' ? '0 4px 16px rgba(255, 193, 7, 0.3)' : 'none'
            }}
          >
            <FileText size={18} />
            {t('expenses.my_notes')}
            </button>

            {user?.role === 'admin' && (
              <>
                <button
                  onClick={() => setActiveTab('validation')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: activeTab === 'validation'
                    ? 'linear-gradient(135deg, #ffc107, #ffd54f)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  position: 'relative',
                  boxShadow: activeTab === 'validation' ? '0 4px 16px rgba(255, 193, 7, 0.3)' : 'none'
                }}
              >
                <CheckCircle size={18} />
                {t('expenses.validation')}
                  {pendingNotes.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}>
                      {pendingNotes.length}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setActiveTab('stats')}
                style={{
                  padding: '12px 24px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: activeTab === 'stats'
                    ? 'linear-gradient(135deg, #ffc107, #ffd54f)'
                    : 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: activeTab === 'stats' ? '0 4px 16px rgba(255, 193, 7, 0.3)' : 'none'
                }}
              >
                <BarChart3 size={18} />
                {t('expenses.statistics')}
                </button>
              </>
            )}
        </div>

        {/* Actions */}
        {activeTab === 'mes-notes' && (
          <div style={{ marginBottom: '24px' }}>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{
                padding: '12px 24px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #ffc107, #ffd54f)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 16px rgba(255, 193, 7, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 193, 7, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 193, 7, 0.3)';
              }}
            >
              <Plus size={18} />
              {t('expenses.new_note')}
            </button>
          </div>
        )}

        {/* Contenu */}
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '256px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '20px',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '3px solid rgba(255, 255, 255, 0.2)',
              borderTop: '3px solid #ffffff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        ) : (
          <div>
            {/* MES NOTES DE FRAIS */}
            {activeTab === 'mes-notes' && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#ffffff',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <FileText size={20} />
                  {t('expenses.my_notes_title')}
                </h2>
                
                <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{(() => { const v = t('expenses.title_field'); return (v && !v.startsWith('expenses.')) ? v : 'Title'; })()}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{(() => { const v = t('expenses.expense_date'); return (v && !v.startsWith('expenses.')) ? v : 'Expense date'; })()}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{(() => { const v = t('expenses.expense_lines'); return (v && !v.startsWith('expenses.')) ? v : 'Expense lines'; })()}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{(() => { const v = t('expenses.total_amount'); return (v && !v.startsWith('expenses.')) ? v : 'Total amount'; })()}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>📎</th>
                          <th className="notes-frais-status-col" style={{ padding: '12px 10px', color: '#374151', fontWeight: '600', minWidth: isMobile ? 100 : undefined }}>{(() => { const v = t('expenses.status'); return (v && !v.startsWith('expenses.')) ? v : 'Status'; })()}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{(() => { const v = t('common.actions'); return (v && !v.startsWith('common.')) ? v : 'Actions'; })()}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notes.map((note) => {
                          const receipts = note.receipt_files ? JSON.parse(note.receipt_files || '[]') : [];
                          return (
                            <tr key={note.id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                              <td style={{ padding: '12px 10px', color: '#111827' }}>
                                <div style={{ fontWeight: '600' }}>{note.title}</div>
                                {note.description && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={note.description}>{note.description}</div>}
                                {normalizeStatus(note.status) === 'rejected' && note.rejection_reason && (
                                  <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <span>⚠️</span>
                                    <button
                                      type="button"
                                      onClick={() => window.alert((t('expenses.rejection_reason') || 'Rejection reason') + ':\n\n' + (note.rejection_reason || '—'))}
                                      style={{ background: 'none', border: 'none', padding: 0, color: '#dc2626', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem' }}
                                    >
                                      {(() => { const lbl = t('expenses.view_rejection_reason'); return (lbl && !lbl.startsWith('expenses.')) ? lbl : 'View reason'; })()}
                                    </button>
                                  </div>
                                )}
                                {note.validation_comment && normalizeStatus(note.status) === 'approved' && <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '4px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={note.validation_comment}>💬 {note.validation_comment}</div>}
                              </td>
                              <td style={{ padding: '12px 10px', color: '#334155' }}>{new Date(note.expense_date).toLocaleDateString(navigator.language || 'fr-FR')}</td>
                              <td style={{ padding: '12px 10px', color: '#374151' }}>{note.items_count}</td>
                              <td style={{ padding: '12px 10px', color: '#111827', fontWeight: '600' }}>
                                {formatNoteAmount(note.total_amount, note.currency)}
                                {note.approved_amount != null && note.approved_amount !== note.total_amount && (
                                  <div style={{ fontSize: '0.75rem', color: '#059669' }}>{t('expenses.approved')}: {formatNoteAmount(note.approved_amount, note.currency)}</div>
                                )}
                              </td>
                              <td style={{ padding: '12px 10px' }}>
                                {receipts.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {receipts.slice(0, 2).map((file, index) => (
                                      <a key={index} href={expenseReceiptHref(file)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#2563eb', textDecoration: 'none' }}>📄</a>
                                    ))}
                                    {receipts.length > 2 && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>+{receipts.length - 2}</span>}
                                  </div>
                                ) : '—'}
                              </td>
                              <td className="notes-frais-status-col" style={{ padding: '12px 10px', minWidth: isMobile ? 100 : undefined }}>
                                <span style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: '600', borderRadius: '12px', backgroundColor: getStatusColor(note.status) + '22', color: getStatusColor(note.status), whiteSpace: 'nowrap' }}>
                                  {getStatusText(note.status)}
                                </span>
                              </td>
                              <td style={{ padding: '12px 10px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                  {normalizeStatus(note.status) === 'draft' && (
                                    <button
                                      type="button"
                                      onClick={() => handleSendReport(note)}
                                      disabled={sendingId === note.id}
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: '#fff',
                                        background: sendingId === note.id ? '#9ca3af' : 'linear-gradient(135deg, #059669, #047857)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: sendingId === note.id ? 'not-allowed' : 'pointer',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {sendingId === note.id ? (t('common.loading') || '...') : ((t('expenses.send_expense_report') || '').startsWith('expenses.') ? 'Send expense report' : (t('expenses.send_expense_report') || t('expenses.send_report') || 'Send expense report'))}
                                    </button>
                                  )}
                                  {normalizeStatus(note.status) === 'rejected' && note.rejection_reason && (
                                    <button
                                      type="button"
                                      onClick={() => window.alert((t('expenses.rejection_reason') || 'Rejection reason') + ':\n\n' + note.rejection_reason)}
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        color: '#fff',
                                        background: '#ef4444',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {(() => { const lbl = t('expenses.view_rejection_reason'); return (lbl && !lbl.startsWith('expenses.')) ? lbl : 'View reason'; })()}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {notes.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '16px' }}>💰</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#ffffff', marginBottom: '8px' }}>
                      {t('expenses.no_notes')}
                    </h3>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('expenses.create_first_note')}</p>
                  </div>
                )}
              </div>
            )}

            {/* VALIDATION (Admin) */}
            {activeTab === 'validation' && user?.role === 'admin' && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: '24px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
              }}>
                <h2 style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#ffffff',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <CheckCircle size={20} />
                    {t('expenses.notes_to_validate')} ({pendingNotes.length})
                  </h2>
                
                <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left', background: '#f8fafc' }}>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('expenses.title_field')}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('expenses.employee')}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('expenses.expense_date')}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('expenses.total_amount')}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('expenses.submitted_on')}</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>📎</th>
                          <th style={{ padding: '12px 10px', color: '#374151', fontWeight: '600' }}>{t('common.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingNotes.map((note) => {
                          const receipts = note.receipt_files ? JSON.parse(note.receipt_files || '[]') : [];
                          return (
                            <tr key={note.id} style={{ borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
                              <td style={{ padding: '12px 10px', color: '#111827' }}>
                                <div style={{ fontWeight: '600' }}>{note.title}</div>
                                {note.description && <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={note.description}>{note.description}</div>}
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px' }}>{note.items_count} {t('expenses.expense_lines')}</div>
                              </td>
                              <td style={{ padding: '12px 10px', color: '#334155' }}>
                                <div style={{ fontWeight: '500' }}>{note.first_name} {note.last_name}</div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{note.employee_code} • {note.department || '—'}</div>
                              </td>
                              <td style={{ padding: '12px 10px', color: '#334155' }}>{new Date(note.expense_date).toLocaleDateString(navigator.language || 'fr-FR')}</td>
                              <td style={{ padding: '12px 10px', color: '#059669', fontWeight: '600' }}>{formatNoteAmount(note.total_amount, note.currency)}</td>
                              <td style={{ padding: '12px 10px', color: '#475569' }}>{note.submitted_at ? new Date(note.submitted_at).toLocaleDateString(navigator.language || 'fr-FR') : '—'}</td>
                              <td style={{ padding: '12px 10px' }}>
                                {receipts.length > 0 ? (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {receipts.slice(0, 2).map((file, index) => (
                                      <a key={index} href={expenseReceiptHref(file)} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem', color: '#2563eb', textDecoration: 'none' }} title={file.originalname}>📄</a>
                                    ))}
                                    {receipts.length > 2 && <span style={{ fontSize: '0.75rem', color: '#64748b' }}>+{receipts.length - 2}</span>}
                                  </div>
                                ) : '—'}
                              </td>
                              <td style={{ padding: '12px 10px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleValidation(note.id, 'approve')}
                                    style={{ padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: '#10b981', border: 'none', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {t('expenses.approve') || 'Approve'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleValidation(note.id, 'reject')}
                                    style={{ padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: '#ef4444', border: 'none', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {t('expenses.refuse') || 'Refuse'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setModifyNote(note); setModifyAmount(String(note.total_amount || '')); setModifyComment(''); }}
                                    style={{ padding: '6px 10px', fontSize: '0.75rem', fontWeight: 600, color: '#fff', background: '#f59e0b', border: 'none', borderRadius: '8px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                  >
                                    {t('expenses.modify_and_approve') || 'Modify & approve'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {pendingNotes.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '16px' }}>✅</div>
                    <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#ffffff', marginBottom: '8px' }}>
                      {t('expenses.no_validation_pending')}
                    </h3>
                    <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t('expenses.all_processed')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Modal: Modify amount & approve */}
            {modifyNote && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }} onClick={() => setModifyNote(null)}>
                <div style={{ background: 'linear-gradient(180deg, #1a1f3a 0%, #0a0e27 100%)', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', border: '1px solid rgba(255,255,255,0.15)' }} onClick={e => e.stopPropagation()}>
                  <h3 style={{ margin: '0 0 16px', color: '#fff', fontSize: '18px' }}>{t('expenses.modify_and_approve') || 'Modify amount & approve'}</h3>
                  <p style={{ margin: '0 0 12px', color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>{modifyNote.title} — {formatNoteAmount(modifyNote.total_amount, modifyNote.currency)}</p>
                  <label style={{ display: 'block', marginBottom: '4px', color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>{t('expenses.approved_amount') || 'Approved amount'}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={modifyAmount}
                    onChange={e => setModifyAmount(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', marginBottom: '12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <label style={{ display: 'block', marginBottom: '4px', color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>{t('expenses.comment_optional') || 'Comment (optional)'}</label>
                  <textarea
                    value={modifyComment}
                    onChange={e => setModifyComment(e.target.value)}
                    rows={3}
                    placeholder={t('expenses.comment_placeholder') || 'Comment for the employee...'}
                    style={{ width: '100%', padding: '10px 12px', marginBottom: '16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button type="button" onClick={() => setModifyNote(null)} style={{ padding: '10px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>{t('common.cancel')}</button>
                    <button
                      type="button"
                      onClick={() => {
                        const amount = modifyAmount.trim() ? parseFloat(modifyAmount) : null;
                        handleValidation(modifyNote.id, 'approve', amount, modifyComment.trim() || null);
                        setModifyNote(null);
                        setModifyAmount('');
                        setModifyComment('');
                      }}
                      style={{ padding: '10px 18px', borderRadius: '8px', border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
                    >
                      {t('expenses.approve') || 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STATISTIQUES (Admin) - responsive: cards on mobile, table on desktop */}
            {activeTab === 'stats' && user?.role === 'admin' && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '20px',
                padding: isMobile ? '16px' : '24px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)',
                width: '100%',
                boxSizing: 'border-box'
              }}>
                <h2 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 700, color: '#ffffff', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <BarChart3 size={20} />
                  {t('expenses.statistics') || 'Statistiques'}
                </h2>
                {stats.overview && (
                  <>
                  {isMobile ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', minWidth: 0 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '14px 16px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', gap: '6px', minWidth: 0 }}>
                        <span style={{ color: '#111827', fontSize: '0.875rem' }}>📄 {t('expenses.notes_total')}</span>
                        <span style={{ fontWeight: 600, color: '#111827', fontSize: '1rem' }}>{stats.overview.total_notes}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '14px 16px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', gap: '6px', minWidth: 0 }}>
                        <span style={{ color: '#111827', fontSize: '0.875rem' }}>⏳ {t('expenses.pending')}</span>
                        <span style={{ fontWeight: 600, color: '#f59e0b', fontSize: '1rem' }}>{stats.overview.pending_count}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '14px 16px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', gap: '6px', minWidth: 0 }}>
                        <span style={{ color: '#111827', fontSize: '0.875rem' }}>💰 {t('expenses.total_amount')}</span>
                        <span style={{ fontWeight: 600, color: '#111827', fontSize: '1rem', wordBreak: 'break-word' }}>{stats.overview.total_amount}€</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', padding: '14px 16px', background: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', gap: '6px', minWidth: 0 }}>
                        <span style={{ color: '#111827', fontSize: '0.875rem' }}>✅ {t('expenses.approved_amount')}</span>
                        <span style={{ fontWeight: 600, color: '#059669', fontSize: '1rem', wordBreak: 'break-word' }}>{stats.overview.approved_amount}€</span>
                      </div>
                    </div>
                  ) : (
                  <div style={{ background: '#ffffff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb', background: '#f8fafc' }}>
                          <th style={{ padding: '12px 16px', textAlign: 'left', color: '#374151', fontWeight: '600' }}>{t('expenses.metric') || 'Indicateur'}</th>
                          <th style={{ padding: '12px 16px', textAlign: 'right', color: '#374151', fontWeight: '600' }}>{t('expenses.value') || 'Valeur'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '12px 16px', color: '#111827' }}>📄 {t('expenses.notes_total')}</td><td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>{stats.overview.total_notes}</td></tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '12px 16px', color: '#111827' }}>⏳ {t('expenses.pending')}</td><td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#f59e0b' }}>{stats.overview.pending_count}</td></tr>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}><td style={{ padding: '12px 16px', color: '#111827' }}>💰 {t('expenses.total_amount')}</td><td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>{stats.overview.total_amount}€</td></tr>
                        <tr><td style={{ padding: '12px 16px', color: '#111827' }}>✅ {t('expenses.approved_amount')}</td><td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>{stats.overview.approved_amount}€</td></tr>
                      </tbody>
                    </table>
                  </div>
                  )}
                </>
                )}
              </div>
            )}
          </div>
        )}

        {/* MODAL CRÉATION NOTE */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: isMobile ? '12px' : '20px',
            boxSizing: 'border-box'
          }}>
            <div style={{
              background: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(30px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: isMobile ? '16px' : '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              maxWidth: '900px',
              width: '100%',
              maxHeight: isMobile ? '85vh' : '90vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              color: '#ffffff',
              boxSizing: 'border-box',
              minWidth: 0
            }}>
              <div style={{
                padding: isMobile ? '16px' : '24px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px'
              }}>
                <h2 style={{
                  fontSize: isMobile ? '1.1rem' : '24px',
                  fontWeight: 700,
                  color: '#ffffff',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minWidth: 0,
                  flex: '1 1 auto'
                }}>
                  <Plus size={isMobile ? 20 : 24} />
                  {t('expenses.new_note')}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#ffffff',
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    fontSize: '20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  }}
                >
                  ✕
                </button>
              </div>

              <div style={{ padding: isMobile ? '16px' : '24px', boxSizing: 'border-box', minWidth: 0 }}>
                {/* Informations générales */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      {t('expenses.title_field')} *
                    </label>
                    <input
                      type="text"
                      value={newNote.title}
                      onChange={(e) => setNewNote(prev => ({ ...prev, title: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      placeholder={t('expenses.title_placeholder')}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      {t('expenses.expense_date')} *
                    </label>
                    <input
                      type="date"
                      value={newNote.expense_date}
                      onChange={(e) => setNewNote(prev => ({ ...prev, expense_date: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      {t('expenses.description')}
                    </label>
                    <textarea
                      value={newNote.description}
                      onChange={(e) => setNewNote(prev => ({ ...prev, description: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        minHeight: '80px'
                      }}
                      rows="2"
                      placeholder={t('expenses.description_placeholder')}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  {/* Upload de factures */}
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      📎 {t('expenses.receipts_upload')}
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/gif,application/pdf"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setSelectedReceipts(files);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    />
                    {selectedReceipts.length > 0 && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '12px'
                      }}>
                        <p style={{ fontSize: '13px', color: '#10b981', marginBottom: '8px', fontWeight: 600 }}>
                          {t('expenses.files_selected', { count: selectedReceipts.length })} :
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {selectedReceipts.map((file, index) => (
                            <div key={index} style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.8)' }}>
                              📄 {file.name} ({(file.size / 1024).toFixed(2)} KB)
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      {t('expenses.purpose')}
                    </label>
                    <input
                      type="text"
                      value={newNote.purpose}
                      onChange={(e) => setNewNote(prev => ({ ...prev, purpose: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      placeholder={t('expenses.purpose_placeholder')}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.8)',
                      marginBottom: '8px'
                    }}>
                      {t('expenses.project_code')}
                    </label>
                    <input
                      type="text"
                      value={newNote.project_code}
                      onChange={(e) => setNewNote(prev => ({ ...prev, project_code: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                      placeholder={t('expenses.project_code_placeholder')}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      }}
                    />
                  </div>
                </div>

                {/* Lignes de frais - même style que les champs du haut */}
                {(() => {
                  const fieldStyle = {
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#ffffff',
                    fontSize: '14px',
                    outline: 'none'
                  };
                  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.8)', marginBottom: '8px' };
                  const focusBlur = (e, isFocus) => {
                    e.currentTarget.style.borderColor = isFocus ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)';
                    e.currentTarget.style.background = isFocus ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)';
                  };
                  return (
                    <div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '12px', minWidth: 0 }}>
                        <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#ffffff', margin: 0, minWidth: 0 }}>{t('expenses.expenses_section')}</h3>
                        <button
                          onClick={addItem}
                          style={{
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#ffffff',
                            padding: isMobile ? '10px 14px' : '8px 16px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            border: 'none',
                            cursor: 'pointer',
                            flexShrink: 0
                          }}
                        >
                          ➕ {t('expenses.add_line')}
                        </button>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {newNote.items.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '12px',
                              padding: isMobile ? '12px' : '16px',
                              background: 'rgba(255, 255, 255, 0.05)',
                              minWidth: 0
                            }}
                          >
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px', minWidth: 0 }}>
                              <h4 style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)', margin: 0, minWidth: 0 }}>{t('expenses.line_number', { number: index + 1 })}</h4>
                              {newNote.items.length > 1 && (
                                <button
                                  onClick={() => removeItem(index)}
                                  type="button"
                                  style={{ fontSize: '13px', color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                                >
                                  🗑️ {t('expenses.remove_line')}
                                </button>
                              )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', minWidth: 0 }}>
                              <div>
                                <label style={labelStyle}>{t('expenses.category')} *</label>
                                <select
                                  value={item.category}
                                  onChange={(e) => updateItem(index, 'category', e.target.value)}
                                  style={{ ...fieldStyle, cursor: 'pointer' }}
                                  onFocus={(e) => focusBlur(e, true)}
                                  onBlur={(e) => focusBlur(e, false)}
                                >
                                  <option value="" style={{ background: '#1a1f3a', color: '#ffffff' }}>{t('expenses.select_category')}</option>
                                  {categories.map((cat) => (
                                    <option key={cat.name} value={cat.name} style={{ background: '#1a1f3a', color: '#ffffff' }}>{cat.key ? t(`expenses.${cat.key}`) : cat.name}</option>
                                  ))}
                                </select>
                              </div>

                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={labelStyle}>{t('expenses.amount')} *</label>
                                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.amount}
                                    onChange={(e) => updateItem(index, 'amount', e.target.value)}
                                    placeholder="0.00"
                                    style={{ ...fieldStyle, flex: 1, minWidth: isMobile ? '80px' : '120px' }}
                                    onFocus={(e) => focusBlur(e, true)}
                                    onBlur={(e) => focusBlur(e, false)}
                                  />
                                  <select
                                    value={item.currency || 'EUR'}
                                    onChange={(e) => updateItem(index, 'currency', e.target.value)}
                                    style={{ ...fieldStyle, width: '160px', cursor: 'pointer' }}
                                    onFocus={(e) => focusBlur(e, true)}
                                    onBlur={(e) => focusBlur(e, false)}
                                  >
                                    {CURRENCIES.map((curr) => (
                                      <option key={curr.code} value={curr.code} style={{ background: '#1a1f3a', color: '#ffffff' }}>{curr.flag} {curr.code} ({curr.symbol})</option>
                                    ))}
                                  </select>
                                </div>
                              </div>

                              <div>
                                <label style={labelStyle}>{t('expenses.merchant_name')}</label>
                                <input
                                  type="text"
                                  value={item.merchant_name}
                                  onChange={(e) => updateItem(index, 'merchant_name', e.target.value)}
                                  placeholder={t('expenses.merchant_name_placeholder')}
                                  style={fieldStyle}
                                  onFocus={(e) => focusBlur(e, true)}
                                  onBlur={(e) => focusBlur(e, false)}
                                />
                              </div>

                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={labelStyle}>{t('expenses.description')} *</label>
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                                  placeholder={t('expenses.description_placeholder')}
                                  style={fieldStyle}
                                  onFocus={(e) => focusBlur(e, true)}
                                  onBlur={(e) => focusBlur(e, false)}
                                />
                              </div>

                              <div>
                                <label style={labelStyle}>{t('expenses.receipt_reference')}</label>
                                <input
                                  type="text"
                                  value={item.receipt_reference}
                                  onChange={(e) => updateItem(index, 'receipt_reference', e.target.value)}
                                  placeholder={t('expenses.receipt_reference_placeholder')}
                                  style={fieldStyle}
                                  onFocus={(e) => focusBlur(e, true)}
                                  onBlur={(e) => focusBlur(e, false)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total */}
                      <div style={{ marginTop: '20px', padding: isMobile ? '12px' : '16px', background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.3)', borderRadius: '12px', minWidth: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#ffffff', minWidth: 0 }}>{t('expenses.total')} :</span>
                          <span style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#93c5fd', wordBreak: 'break-word' }}>
                            {totalCurrency === 'EUR' || totalCurrency === 'USD' || totalCurrency === 'GBP' ? `${totalSymbol}${totalFormatted}` : `${totalFormatted} ${totalSymbol}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={{
                padding: isMobile ? '16px' : '24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 20px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flex: isMobile ? '1 1 120px' : undefined
                  }}
                >
                  {t('expenses.cancel')}
                </button>
                <button
                  onClick={handleCreateNote}
                  disabled={!newNote.title || !newNote.expense_date || newNote.items.some(item => !item.category || !item.description || !item.amount)}
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 20px',
                    background: !newNote.title || !newNote.expense_date || newNote.items.some(item => !item.category || !item.description || !item.amount) ? 'rgba(255,255,255,0.2)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: !newNote.title || !newNote.expense_date || newNote.items.some(item => !item.category || !item.description || !item.amount) ? 'not-allowed' : 'pointer',
                    flex: isMobile ? '1 1 120px' : undefined
                  }}
                >
                  💾 {t('expenses.save')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (max-width: 768px) {
          .notes-frais-header {
            position: relative !important;
            top: auto !important;
            left: auto !important;
            right: auto !important;
          }
        }
        @media (min-width: 769px) {
          .notes-frais-header {
            position: fixed !important;
          }
          .notes-frais-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .notes-frais-content {
            padding: 180px 60px 60px !important;
          }
        }
        .notes-frais-content {
          animation: slideUp 0.4s ease-out;
        }
        .notes-frais-status-col {
          min-width: 100px;
        }
        @media (max-width: 768px) {
          .notes-frais-status-col {
            min-width: 100px;
          }
        }
      `}</style>
    </div>
  );
};

export default NotesFraisManagement;
